import { ng } from "@common/ngds";
import { Drawer, Point } from "@common/components/Rendering";
import { ArrowId, load_position_style } from "../ViewportStyles";
import type { Extent } from "./drawGeomCrossSection";

// The buckling "Load position" symbol: two triangles that meet in a single point at the load
// position, drawn on the centerline of the cross-section. The triangle tips mark the exact point
// the load acts at, and the triangle that ends up outside the section says which face it is.
//
// The symbol itself (shape, size, colors) is defined as an svg symbol in ViewportStyles.tsx, which
// keeps its size in screen pixels - constant for every section and at every zoom level. This file
// only decides where to put it and which way up it goes.

interface BucklingLoadPosition {
  alignment: ng.beam.Alignment;
  isTimber: boolean;
}

/**
 * Load position of an upright section: the symbol stands on the vertical centerline and the
 * alignment picks the height.
 *
 * Cross-section geometry is drawn as [x, -y] (see drawGeomCrossSection.ts), so ver.min is the
 * top of the section and ver.max the bottom. ALIGNMENT_CENTER maps to the section origin, which
 * is where the y/z coordinate system is drawn, i.e. the center of gravity.
 *
 * Returns null when no load position is selected, so nothing is drawn - this matches the
 * "Load position" dropdown, which shows no selection for ALIGNMENT_UNSPECIFIED.
 */
const uprightPoint = (geom: Extent, alignment: ng.beam.Alignment): Point | null => {
  const hor = (geom.hor.min + geom.hor.max) / 2;
  switch (alignment) {
    case ng.beam.Alignment.ALIGNMENT_TOP:
      return [hor, geom.ver.min];
    case ng.beam.Alignment.ALIGNMENT_CENTER:
      return [hor, 0];
    case ng.beam.Alignment.ALIGNMENT_BOTTOM:
      return [hor, geom.ver.max];
    default:
      return null;
  }
};

/**
 * Load position of a section laid on its side: the symbol sits on the horizontal centerline and
 * the alignment picks the side face instead of the height - bottom is the left face, top the
 * right one, center still the section origin.
 */
const sidewaysPoint = (geom: Extent, alignment: ng.beam.Alignment): Point | null => {
  const ver = (geom.ver.min + geom.ver.max) / 2;
  switch (alignment) {
    case ng.beam.Alignment.ALIGNMENT_TOP:
      return [geom.hor.max, ver];
    case ng.beam.Alignment.ALIGNMENT_CENTER:
      return [0, ver];
    case ng.beam.Alignment.ALIGNMENT_BOTTOM:
      return [geom.hor.min, ver];
    default:
      return null;
  }
};

/**
 * Read the buckling load position of the primary beam. Only steel and timber have a "Load
 * position" dropdown in the Buckling accordion (steel: lateral torsional buckling, timber:
 * lateral torsional and shear buckling), so the beam element oneof itself decides where to read
 * it from - concrete beams have no load position and draw nothing.
 */
export const bucklingLoadPosition = (projectData: ng.frontend.column.Project | null): BucklingLoadPosition => {
  const beam = projectData?.data?.input?.structures?.[0]?.elements?.[0]?.getBeam();

  const timber = beam?.getTimber() ?? null;
  if (timber !== null) {
    return { alignment: timber.loadPosition, isTimber: true };
  }

  const steel = beam?.getSteel() ?? null;
  if (steel !== null) {
    return { alignment: steel.loadPosition, isTimber: false };
  }

  return { alignment: ng.beam.Alignment.ALIGNMENT_UNSPECIFIED, isTimber: false };
};

/**
 * Draw the load position symbol for the selected buckling load position. Exactly one symbol is
 * drawn (top edge, center of gravity or bottom edge) - or none, when no position is selected.
 *
 * A timber section that is wider than it is tall is laid flat, so the load acts on a side face
 * rather than on the top or bottom one: the symbol is turned a quarter turn clockwise and the
 * alignment is read across the section instead of up it (bottom = left face, center = center,
 * top = right face). Turning it clockwise keeps the color that ends up outside the section the
 * same as when upright.
 */
export const drawLoadPosition = (drawer: Drawer<ArrowId>, geom: Extent, position: BucklingLoadPosition) => {
  const width = geom.hor.max - geom.hor.min;
  const height = geom.ver.max - geom.ver.min;
  const sideways = position.isTimber && width > height;

  const point = sideways ? sidewaysPoint(geom, position.alignment) : uprightPoint(geom, position.alignment);
  if (point === null) {
    return;
  }

  drawer.drawSymbol(point, "load-position", load_position_style, sideways ? { degrees: 90 } : undefined);
};
